/**
 * Theme mode — resolution, persistence and application.
 *
 * Three-state model:
 *   'light' | 'dark'  an explicit choice the user made; persisted.
 *   'system'          no explicit choice; follow the OS live.
 *
 * Resolution order is: explicit stored choice > OS preference. The absence
 * of a stored value IS 'system' — we never write 'system' to storage, so a
 * user who has never chosen (or who resets to system) transparently picks up
 * OS changes on every device.
 *
 * Application is a single `data-theme` attribute on <html>, which is what
 * `[data-theme='dark']` in design-system/colors_and_type.css keys off.
 *
 * ⚠️ public/theme-boot.js duplicates THEME_STORAGE_KEY / THEME_ATTRIBUTE /
 * DARK_MEDIA_QUERY and the resolution rules, because it has to run before the
 * bundle loads (see that file's header). The duplication is asserted in
 * __tests__/theme-mode.test.tsx — change one, change the other.
 */

export type ThemeMode = 'light' | 'dark' | 'system'

/** The theme actually applied to the document — 'system' is always resolved away. */
export type ResolvedTheme = 'light' | 'dark'

export const THEME_STORAGE_KEY = 'staffology_theme_mode'
export const THEME_ATTRIBUTE = 'data-theme'
export const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)'

/**
 * Master switch for the whole dark-mode mechanism.
 *
 * Dark mode is fully BUILT (role tokens, provider, tenant seam, migrated DS components)
 * but deliberately NOT enabled: while this is `false` the app is light-only regardless of
 * the user's OS `prefers-color-scheme` or any stored choice, so nothing lands in dark until
 * the palette is reviewed and an in-app toggle ships. Flip to `true` — and mirror the same
 * literal in `public/theme-boot.js` — to turn it on. The resolution machinery below stays
 * pure and tested so it is ready the moment the switch flips.
 */
export const DARK_MODE_ENABLED = false

const THEME_MODES: readonly ThemeMode[] = ['light', 'dark', 'system']

export function isThemeMode(value: unknown): value is ThemeMode {
  return typeof value === 'string' && (THEME_MODES as readonly string[]).includes(value)
}

/**
 * The OS-level preference right now. Falls back to 'light' when matchMedia is
 * unavailable (old browsers, jsdom without a mock, SSR-ish environments).
 */
export function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'light'
  try {
    return window.matchMedia(DARK_MEDIA_QUERY).matches ? 'dark' : 'light'
  } catch {
    return 'light'
  }
}

/**
 * The user's persisted choice, or 'system' when they have not made one.
 * Storage access is wrapped: Safari private mode and hardened enterprise
 * profiles throw on localStorage rather than returning null.
 */
export function readStoredMode(): ThemeMode {
  if (typeof window === 'undefined') return 'system'
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY)
    return isThemeMode(stored) ? stored : 'system'
  } catch {
    return 'system'
  }
}

/**
 * Persist an explicit choice. Writing 'system' CLEARS the key — "no stored
 * value" is the canonical representation of "follow the OS".
 */
export function writeStoredMode(mode: ThemeMode): void {
  if (typeof window === 'undefined') return
  try {
    if (mode === 'system') window.localStorage.removeItem(THEME_STORAGE_KEY)
    else window.localStorage.setItem(THEME_STORAGE_KEY, mode)
  } catch {
    // Non-fatal: the theme still applies for this session, it just won't persist.
  }
}

/** Collapse the three-state mode into the two-state theme actually rendered. */
export function resolveTheme(mode: ThemeMode, systemTheme: ResolvedTheme): ResolvedTheme {
  return mode === 'system' ? systemTheme : mode
}

/**
 * Write the theme to the document. Also sets `color-scheme` in dark so native
 * widgets (scrollbars, date pickers, form controls) follow.
 *
 * `color-scheme` is only ever SET for dark and cleared for light — leaving it
 * unset in light keeps the light rendering identical to before this existed.
 */
export function applyResolvedTheme(
  resolved: ResolvedTheme,
  root: HTMLElement | null = typeof document === 'undefined' ? null : document.documentElement,
): void {
  if (!root) return
  root.setAttribute(THEME_ATTRIBUTE, resolved)
  root.style.colorScheme = resolved === 'dark' ? 'dark' : ''
}

/**
 * Subscribe to live OS preference changes. Returns an unsubscribe function.
 * Callers should only act on this while in 'system' mode.
 */
export function subscribeToSystemTheme(listener: (theme: ResolvedTheme) => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {}

  let query: MediaQueryList
  try {
    query = window.matchMedia(DARK_MEDIA_QUERY)
  } catch {
    return () => {}
  }

  const handler = (event: MediaQueryListEvent) => listener(event.matches ? 'dark' : 'light')

  if (typeof query.addEventListener === 'function') {
    query.addEventListener('change', handler)
    return () => query.removeEventListener('change', handler)
  }
  // Safari < 14 / older WebKit only has the deprecated API.
  if (typeof query.addListener === 'function') {
    query.addListener(handler)
    return () => query.removeListener(handler)
  }
  return () => {}
}
